import { useState, useEffect, useRef } from "react";
import ServicePlanAPI from '~/api/servicePlanApi';
import UserRoleAPI from '~/api/userRoleApi';

export default function useCreateUserAccountModal(setValue, getValues, codeValues) {
    const [step, setStep] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [successData, setSuccessData] = useState(null);
    const [servicePlans, setServicePlans] = useState([]);
    const [resendCountdown, setResendCountdown] = useState(60);
    
    const inputsRef = useRef([]);

    useEffect(() => {
        Promise.all([
            ServicePlanAPI.getByServiceType(1),
            ServicePlanAPI.getByServiceType(2)
        ]).then(([res1, res2]) => {
            setServicePlans([...res1.data, ...res2.data]);
        }).catch(() => {});
    }, []);

    useEffect(() => {
        let timer;
        if (step === 2 && resendCountdown > 0) {
            timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
        }
        return () => clearTimeout(timer);
    }, [step, resendCountdown]);

    const resetModal = (resetForm) => {
        setStep(1);
        setError(null);
        setSuccessData(null);
        setIsSubmitting(false);
        setResendCountdown(60);
        resetForm({
            Email: "",
            ServicePlanId: "",
            SuperAdminPassword: "",
            code0: "", code1: "", code2: "", code3: "", code4: "", code5: ""
        });
    };

    const handleInput = (e, index) => {
        const value = e.target.value;
        if (/^\d$/.test(value)) {
            setValue(`code${index}`, value);
            if (index < 5) inputsRef.current[index + 1].focus();
        } else {
            setValue(`code${index}`, "");
        }
    };

    const handleBeforeInput = (e) => {
        if (!/^\d$/.test(e.data)) {
            e.preventDefault();
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === "Backspace") {
            const currentValue = getValues(`code${index}`);
            if (!currentValue && index > 0) {
                setValue(`code${index - 1}`, "");
                inputsRef.current[index - 1].focus();
            } else {
                setValue(`code${index}`, "");
            }
        }
    };

    const sendCreationCode = (data) => {
        setIsSubmitting(true);
        setError(null);
        UserRoleAPI.sendCreationCode({
            Email: data.Email,
            ServicePlanId: parseInt(data.ServicePlanId) || 0,
            SuperAdminPassword: data.SuperAdminPassword
        })
        .then(res => {
            if (res.data?.Succeeded === false) {
                setError(res.data.Message);
            } else {
                setStep(2);
                setResendCountdown(60);
            }
        })
        .catch(err => {
            setError("Failed to send verification code.");
        })
        .finally(() => {
            setIsSubmitting(false);
        });
    };

    const createUserWithCode = (data) => {
        const code = codeValues.join("");
        if (code.length < 6) return;
        
        setIsSubmitting(true);
        setError(null);
        UserRoleAPI.createUserWithCode({
            Email: data.Email,
            ServicePlanId: parseInt(data.ServicePlanId) || 0,
            SuperAdminPassword: data.SuperAdminPassword,
            VerificationCode: code
        })
        .then(res => {
            if (res.data?.Succeeded === false) {
                setError(res.data.Message);
            } else {
                setSuccessData(res.data);
                setStep(3);
            }
        })
        .catch(err => {
            setError("Failed to create user.");
        })
        .finally(() => {
            setIsSubmitting(false);
        });
    };

    return {
        step,
        isSubmitting,
        error,
        successData,
        servicePlans,
        inputsRef,
        handleInput,
        handleBeforeInput,
        handleKeyDown,
        sendCreationCode,
        createUserWithCode,
        resetModal,
        resendCountdown
    };
}
